/**
 * Personel dashboard'unda avans durumu kartı.
 * Aktif talep yoksa: "Yeni talep" butonu
 * Bekleyen: durum + iptal
 * Karşı teklif: kabul/red
 * Onaylandı: ödeme bekleniyor + iptal
 * Geçmiş: liste
 */

import { useState, useEffect, useCallback } from 'react'
import {
  CreditCard, Plus, Clock, CheckCircle2, XCircle,
  AlertCircle, TrendingUp, X,
} from 'lucide-react'
import {
  personelAvanslari,
  avansKarsiTeklifKabul,
  avansKarsiTeklifRed,
  avansIptal,
  type Avans,
} from '@/lib/services/avans-service'
import { useAuth } from '@/contexts/AuthContext'
import { ConfirmDialog } from '@/components/ui/ConfirmDialog'
import { AvansTalepModal } from './AvansTalepModal'
import type { KullaniciPersonel } from '@/types'

interface Props {
  personel: KullaniciPersonel
}

export function AvansKarti({ personel }: Props) {
  const { firebaseUser } = useAuth()
  const [avanslar, setAvanslar] = useState<Avans[]>([])
  const [yukleniyor, setYukleniyor] = useState(true)
  const [talepAcik, setTalepAcik] = useState(false)
  const [iptalEdilecek, setIptalEdilecek] = useState<Avans | null>(null)
  const [karsiTeklifReddet, setKarsiTeklifReddet] = useState<Avans | null>(null)
  const [hata, setHata] = useState<string | null>(null)

  const yukle = useCallback(async () => {
    try {
      setYukleniyor(true)
      const liste = await personelAvanslari(personel.id)
      setAvanslar(liste)
    } catch (err) {
      console.error(err)
    } finally {
      setYukleniyor(false)
    }
  }, [personel.id])

  useEffect(() => {
    yukle()
  }, [yukle])

  const aktif = avanslar.find((a) =>
    a.durum === 'bekliyor' || a.durum === 'karsiTeklif' || a.durum === 'onaylandi'
  )

  async function karsiTeklifKabul(avans: Avans) {
    try {
      await avansKarsiTeklifKabul(avans.id)
      await yukle()
    } catch (err) {
      setHata((err as Error).message)
    }
  }

  async function karsiTeklifRedOnay() {
    if (!karsiTeklifReddet) return
    try {
      await avansKarsiTeklifRed(karsiTeklifReddet.id)
      setKarsiTeklifReddet(null)
      await yukle()
    } catch (err) {
      setHata((err as Error).message)
      setKarsiTeklifReddet(null)
    }
  }

  async function iptalOnay() {
    if (!iptalEdilecek || !firebaseUser) return
    try {
      await avansIptal(iptalEdilecek.id, firebaseUser.uid)
      setIptalEdilecek(null)
      await yukle()
    } catch (err) {
      setHata((err as Error).message)
      setIptalEdilecek(null)
    }
  }

  if (yukleniyor) {
    return (
      <div className="card text-center py-4">
        <div className="inline-block animate-spin rounded-full h-5 w-5 border-b-2 border-brand-600"></div>
      </div>
    )
  }

  return (
    <>
      <div className="card">
        <div className="flex items-center gap-2 mb-3">
          <CreditCard className="w-5 h-5 text-brand-600" />
          <h2 className="font-bold text-gray-900">Avans</h2>
        </div>

        {hata && (
          <div className="bg-red-50 border border-red-200 text-red-800 text-xs rounded-lg p-2 mb-3 flex justify-between items-start gap-2">
            <span>{hata}</span>
            <button onClick={() => setHata(null)} className="text-red-700">
              <X className="w-3 h-3" />
            </button>
          </div>
        )}

        {aktif ? (
          <AktifAvansGorunum
            avans={aktif}
            onKarsiTeklifKabul={() => karsiTeklifKabul(aktif)}
            onKarsiTeklifRed={() => setKarsiTeklifReddet(aktif)}
            onIptal={() => setIptalEdilecek(aktif)}
          />
        ) : (
          <button
            onClick={() => setTalepAcik(true)}
            className="w-full py-3 bg-brand-50 border-2 border-dashed border-brand-300 text-brand-700 font-semibold rounded-xl flex items-center justify-center gap-2 hover:bg-brand-100 transition-colors"
          >
            <Plus className="w-5 h-5" />
            Yeni Avans Talebi
          </button>
        )}

        {avanslar.length > 0 && (
          <details className="mt-4">
            <summary className="text-xs font-semibold text-gray-500 cursor-pointer">
              Geçmiş ({avanslar.length} kayıt)
            </summary>
            <div className="mt-2 space-y-1.5">
              {avanslar.map((a) => (
                <GecmisAvans key={a.id} avans={a} />
              ))}
            </div>
          </details>
        )}
      </div>

      <AvansTalepModal
        acik={talepAcik}
        personel={personel}
        onKapat={() => setTalepAcik(false)}
        onKaydedildi={() => {
          setTalepAcik(false)
          yukle()
        }}
      />

      <ConfirmDialog
        acik={!!iptalEdilecek}
        baslik="Avansı İptal Et"
        mesaj={`${iptalEdilecek?.durum === 'onaylandi' ? 'Onaylanmış' : 'Bekleyen'} avans talebini iptal etmek istiyor musun?`}
        onayMetni="İptal Et"
        tehlikeli
        onOnay={iptalOnay}
        onIptal={() => setIptalEdilecek(null)}
      />

      <ConfirmDialog
        acik={!!karsiTeklifReddet}
        baslik="Karşı Teklifi Reddet"
        mesaj={`${karsiTeklifReddet?.karsiTeklifTutari ?? 0} ₺ teklifini reddediyorsun. Talep iptal edilecek.`}
        onayMetni="Reddet"
        tehlikeli
        onOnay={karsiTeklifRedOnay}
        onIptal={() => setKarsiTeklifReddet(null)}
      />
    </>
  )
}

interface AktifProps {
  avans: Avans
  onKarsiTeklifKabul: () => void
  onKarsiTeklifRed: () => void
  onIptal: () => void
}

function AktifAvansGorunum({ avans, onKarsiTeklifKabul, onKarsiTeklifRed, onIptal }: AktifProps) {
  if (avans.durum === 'bekliyor') {
    return (
      <div className="bg-amber-50 border border-amber-200 rounded-xl p-3">
        <div className="flex items-center gap-2 mb-2">
          <Clock className="w-4 h-4 text-amber-700" />
          <p className="font-semibold text-amber-900 text-sm">BOSS kararı bekleniyor</p>
        </div>
        <p className="text-2xl font-bold text-amber-900">
          {avans.talepTutari.toLocaleString('de-DE')} ₺
        </p>
        {avans.aciklama && (
          <p className="text-xs text-amber-800 mt-1 italic">"{avans.aciklama}"</p>
        )}
        <button
          onClick={onIptal}
          className="mt-3 text-xs text-red-700 hover:underline"
        >
          Talebi geri çek
        </button>
      </div>
    )
  }

  if (avans.durum === 'karsiTeklif') {
    return (
      <div className="bg-purple-50 border-2 border-purple-300 rounded-xl p-3">
        <div className="flex items-center gap-2 mb-2">
          <TrendingUp className="w-4 h-4 text-purple-700" />
          <p className="font-semibold text-purple-900 text-sm">BOSS karşı teklif gönderdi</p>
        </div>
        <div className="bg-white/60 rounded-lg p-3 mb-3">
          <div className="flex justify-between items-center mb-1">
            <span className="text-xs text-purple-700">Senin talep ettiğin:</span>
            <span className="text-purple-900 line-through">
              {avans.talepTutari.toLocaleString('de-DE')} ₺
            </span>
          </div>
          <div className="flex justify-between items-center">
            <span className="text-xs text-purple-700">BOSS'un teklifi:</span>
            <span className="text-2xl font-bold text-purple-900">
              {(avans.karsiTeklifTutari ?? 0).toLocaleString('de-DE')} ₺
            </span>
          </div>
        </div>
        <div className="grid grid-cols-2 gap-2">
          <button
            onClick={onKarsiTeklifRed}
            className="py-2 bg-white border border-red-300 text-red-700 hover:bg-red-50 rounded-lg text-sm font-semibold"
          >
            Reddet
          </button>
          <button
            onClick={onKarsiTeklifKabul}
            className="py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg text-sm font-semibold"
          >
            Kabul Et
          </button>
        </div>
      </div>
    )
  }

  // onaylandi
  return (
    <div className="bg-green-50 border border-green-200 rounded-xl p-3">
      <div className="flex items-center gap-2 mb-2">
        <CheckCircle2 className="w-4 h-4 text-green-700" />
        <p className="font-semibold text-green-900 text-sm">
          Onaylandı, BOSS'tan ödeme bekleniyor
        </p>
      </div>
      <p className="text-2xl font-bold text-green-900">
        {(avans.onayTutari ?? avans.talepTutari).toLocaleString('de-DE')} ₺
      </p>
      <p className="text-xs text-green-700 mt-1">
        BOSS para verince burada "Ödendi" görünecek.
      </p>
      <button
        onClick={onIptal}
        className="mt-3 text-xs text-red-700 hover:underline"
      >
        Talebi geri çek (henüz ödenmediği için iptal edilebilir)
      </button>
    </div>
  )
}

function GecmisAvans({ avans }: { avans: Avans }) {
  const tarih = avans.talepTarihi.toDate().toLocaleDateString('tr-TR', {
    day: 'numeric',
    month: 'short',
  })

  let renk = 'text-gray-600'
  let ikon = <Clock className="w-3 h-3" />
  let etiket = 'Bekliyor'

  switch (avans.durum) {
    case 'odendi':
      renk = 'text-green-700'
      ikon = <CheckCircle2 className="w-3 h-3" />
      etiket = 'Ödendi'
      break
    case 'reddedildi':
      renk = 'text-red-700'
      ikon = <XCircle className="w-3 h-3" />
      etiket = 'Red'
      break
    case 'iptal':
      renk = 'text-gray-500'
      ikon = <X className="w-3 h-3" />
      etiket = 'İptal'
      break
    case 'onaylandi':
      renk = 'text-blue-700'
      ikon = <Clock className="w-3 h-3" />
      etiket = 'Ödeme bekliyor'
      break
    case 'karsiTeklif':
      renk = 'text-purple-700'
      ikon = <AlertCircle className="w-3 h-3" />
      etiket = 'Karşı teklif'
      break
  }

  const tutar = avans.onayTutari ?? avans.karsiTeklifTutari ?? avans.talepTutari

  return (
    <div className="flex items-center justify-between text-xs py-1.5 px-2 bg-gray-50 rounded-lg">
      <span className="text-gray-500">{tarih}</span>
      <span className="font-medium text-gray-900">
        {tutar.toLocaleString('de-DE')} ₺
      </span>
      <span className={`flex items-center gap-1 ${renk} font-medium`}>
        {ikon}
        {etiket}
      </span>
    </div>
  )
}
